import React from 'react';
import type { ChatProposal } from '../../../api/agentChat/interface';
import {
  approveProposal,
  rejectProposal,
} from '../../../api/agentChat/threads/client';
import {
  type DisplayMessage,
  TERMINAL_PROPOSAL_STATUSES,
  hasPendingProposals,
} from '../chatHelpers';

interface Result {
  pendingPanel: { messageId: string; proposals: ChatProposal[] } | null;
  proposalSubmitting: Record<string, boolean>;
  handleApprove: (messageId: string, proposalId: string) => Promise<void>;
  handleReject: (messageId: string, proposalId: string) => Promise<void>;
}

// Owns proposal approve/reject actions and derives the pending-panel descriptor
// from the latest assistant message. Pending status is a 1-message lookback
// because new assistant messages supersede older pending proposals on screen.
export function useProposalActions(
  dossierId: string,
  threadId: string,
  displayMessages: DisplayMessage[],
  setDisplayMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>,
  handleThreadGone: (err: unknown) => boolean
): Result {
  const [proposalSubmitting, setProposalSubmitting] = React.useState<
    Record<string, boolean>
  >({});

  const pendingPanel = React.useMemo<{
    messageId: string;
    proposals: ChatProposal[];
  } | null>(() => {
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const m = displayMessages[i];
      if (m.role === 'ASSISTANT') {
        if (hasPendingProposals(m.proposals)) {
          return {
            messageId: m.message_id,
            proposals: (m.proposals ?? []).filter(
              (p) => !TERMINAL_PROPOSAL_STATUSES.includes(p.status)
            ),
          };
        }
        return null;
      }
    }
    return null;
  }, [displayMessages]);

  const updateProposal = React.useCallback(
    (messageId: string, proposalId: string, patch: Partial<ChatProposal>) => {
      setDisplayMessages((prev) =>
        prev.map((m) => {
          if (m.message_id !== messageId || !m.proposals) {
            return m;
          }
          return {
            ...m,
            proposals: m.proposals.map((p) =>
              p.proposal_id === proposalId ? { ...p, ...patch } : p
            ),
          };
        })
      );
    },
    [setDisplayMessages]
  );

  const submit = React.useCallback(
    async (
      messageId: string,
      proposalId: string,
      call: () => Promise<{ proposal: ChatProposal }>
    ) => {
      setProposalSubmitting((prev) => ({ ...prev, [proposalId]: true }));
      try {
        const res = await call();
        updateProposal(messageId, proposalId, res.proposal);
      } catch (err) {
        handleThreadGone(err);
      } finally {
        setProposalSubmitting((prev) => {
          const next = { ...prev };
          delete next[proposalId];
          return next;
        });
      }
    },
    [updateProposal, handleThreadGone]
  );

  const handleApprove = React.useCallback(
    (messageId: string, proposalId: string) =>
      submit(messageId, proposalId, () =>
        approveProposal(dossierId, threadId, proposalId)
      ),
    [dossierId, threadId, submit]
  );

  const handleReject = React.useCallback(
    (messageId: string, proposalId: string) =>
      submit(messageId, proposalId, () =>
        rejectProposal(dossierId, threadId, proposalId)
      ),
    [dossierId, threadId, submit]
  );

  return { pendingPanel, proposalSubmitting, handleApprove, handleReject };
}
